import {
  CheckCircleFilled,
  DeleteOutlined,
  FilePdfOutlined,
  LockOutlined,
  PlusOutlined,
  UnlockOutlined,
} from '@ant-design/icons';
import {
  describeError,
  dirhams,
  IdTag,
  isNegative,
  present,
  TYPE_LABELS,
} from '@horof/shared';
import {
  Alert,
  App,
  Button,
  Input,
  Segmented,
  Select,
  Skeleton,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useClient, useQuery } from 'urql';
import { BankImport } from './BankImport';
import {
  BANK_ERRORS,
  bankContext,
  CloseStatementDocument,
  DeleteStatementDocument,
  IgnoreLineDocument,
  type Line,
  lineState,
  linesCount,
  MatchLineDocument,
  OperationFromLineDocument,
  ReopenStatementDocument,
  type Statement,
  StatementDocument,
  type StatementRow,
  StatementsDocument,
  SuggestionsDocument,
  TransferFromLineDocument,
  UnmatchLineDocument,
} from './bank-data';
import { useCanWrite, useLists } from './data';
import { DeskPanel, useSplit } from './panel';
import { CategoryPicker, CounterpartyPicker, ProjectPicker } from './pickers';
import { useFitColumns } from './table-fit';
import { ListHeader, PageHeader } from './ui';

const STATUS = {
  open: <Tag color="orange">مفتوح</Tag>,
  matched: (
    <Tag color="green" icon={<LockOutlined />}>
      مغلق
    </Tag>
  ),
};

/** «من 2026-10-01 إلى 2026-10-31»: words, as an arrow reads backwards in RTL. */
const Period = ({ from, to }: { from: string; to: string }) => (
  <span>
    من <span className="ltr-num">{from}</span> إلى{' '}
    <span className="ltr-num">{to}</span>
  </span>
);

const Amount = ({ amount }: { amount: string }) => (
  <strong
    className={`desk-amount ${isNegative(amount) ? 'is-debit' : 'is-credit'}`}
  >
    {isNegative(amount) ? '−' : '+'} {dirhams(amount.replace(/^-/, ''))}
  </strong>
);

/**
 * «كشوف البنك»: the imported statements, how far their matching is, and the
 * difference with the records (arqam's, doc 57). A row opens the statement.
 */
export function BankScreen() {
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const [importing, setImporting] = useState(false);
  const { ref: fitRef, fit } = useFitColumns();
  const [{ data, error, fetching }] = useQuery({
    query: StatementsDocument,
    context: useMemo(() => bankContext, []),
  });
  const rows = present(data?.bankStatements?.nodes);

  return (
    <section className="desk-page" ref={fitRef}>
      <ListHeader
        title="كشوف البنك"
        count={data ? rows.length : undefined}
        description="كل سطر من الكشف يُطابَق مع عملية أو تحويل مسجَّل، أو يُسجَّل منه، أو يُتجاهَل بسبب. حين لا يبقى سطر ويكون الفرق صفراً، يُغلق الكشف."
        actions={
          canWrite && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setImporting(true)}
            >
              استيراد كشف
            </Button>
          )
        }
      />
      <div className="ui-table-card">
        {error && (
          <Alert
            type="error"
            showIcon
            message={describeError(error, 'تعذّر تحميل الكشوف.')}
          />
        )}
        <Table<StatementRow>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          dataSource={rows}
          loading={fetching && !data}
          pagination={false}
          rowClassName={() => 'is-clickable'}
          onRow={(s) => ({ onClick: () => navigate(`/bank/${s.id}`) })}
          locale={{ emptyText: 'لم يُستورد أي كشف بعد' }}
          columns={fit<StatementRow>([
            {
              key: 'account',
              title: 'الحساب',
              render: (_, s) => s.account?.name,
            },
            {
              key: 'period',
              title: 'الفترة',
              width: 220,
              render: (_, s) => (
                <Period from={s.periodStart} to={s.periodEnd} />
              ),
            },
            {
              key: 'balances',
              drop: 1,
              title: 'الافتتاحي ← الختامي',
              width: 260,
              render: (_, s) => (
                <span>
                  {dirhams(s.openingBalance)} ← {dirhams(s.closingBalance)}
                </span>
              ),
            },
            {
              key: 'progress',
              drop: 2,
              title: 'الأسطر',
              width: 160,
              render: (_, s) =>
                s.unmatchedCount ? (
                  <span>
                    بقي <strong className="ltr-num">{s.unmatchedCount}</strong>{' '}
                    من{' '}
                    <span className="ltr-num">
                      {s.unmatchedCount + s.matchedCount}
                    </span>
                  </span>
                ) : (
                  <span className="is-credit">
                    <CheckCircleFilled /> كلها
                  </span>
                ),
            },
            {
              key: 'difference',
              title: 'الفرق مع الحسابات',
              width: 160,
              render: (_, s) => (
                <span
                  className={Number(s.difference) ? 'is-debit' : 'is-credit'}
                >
                  {dirhams(s.difference)}
                </span>
              ),
            },
            {
              key: 'status',
              title: 'الحالة',
              width: 90,
              render: (_, s) => STATUS[s.status],
            },
          ])}
        />
      </div>
      {importing && <BankImport onClose={() => setImporting(false)} />}
    </section>
  );
}

/**
 * One statement: its lines, the ones left first. A line opens beside the
 * list with arqam's suggestions (same amount, within 7 days), or to record
 * it as an operation or a transfer, or to ignore it. After each, the next
 * line left opens.
 */
export function StatementScreen() {
  const { id = '' } = useParams();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const client = useClient();
  const { modal, message } = App.useApp();
  const split = useSplit();
  const { ref: fitRef, fit } = useFitColumns();
  const [params, setParams] = useSearchParams();
  const show = params.get('show');
  const [project, setProject] = useState<string | null>(null);
  const [{ data, error }] = useQuery({
    query: StatementDocument,
    variables: { id },
    context: useMemo(() => bankContext, []),
  });
  const s = data?.bankStatement;
  const lines = present(s?.lines.nodes);
  // Nothing left (a finished statement): all its lines.
  const all = show === 'all' || (show !== 'left' && s?.unmatchedCount === 0);
  const shown = all ? lines : lines.filter((l) => lineState(l) === 'open');
  const open = s?.status === 'open';
  const line = lines.find((l) => l.id === split.panel) ?? null;

  /** The next line left after `lineNo` (else the first left), or none. */
  const next = (lineNo: number) => {
    const left = lines.filter(
      (l) => lineState(l) === 'open' && l.id !== split.panel,
    );
    return (left.find((l) => l.lineNo > lineNo) ?? left[0])?.id ?? null;
  };

  async function act(
    run: () => Promise<{ error?: Parameters<typeof describeError>[0] }>,
    done: string,
  ) {
    const r = await run();
    if (r.error) {
      message.error(describeError(r.error, 'تعذّر الحفظ.', BANK_ERRORS), 6);
      return false;
    }
    message.success(done);
    return true;
  }

  if (error) {
    return (
      <section className="desk-page">
        <Alert
          type="error"
          showIcon
          message={describeError(error, 'تعذّر تحميل الكشف.')}
        />
      </section>
    );
  }
  if (!data) {
    return (
      <section className="desk-page">
        <Skeleton active paragraph={{ rows: 12 }} />
      </section>
    );
  }
  if (!s) {
    return (
      <section className="desk-page">
        <Alert type="warning" showIcon message="الكشف غير موجود" />
        <Link to="/bank">كشوف البنك</Link>
      </section>
    );
  }

  const closeBlocked = s.unmatchedCount
    ? `بقيت ${linesCount(s.unmatchedCount)} بلا مطابقة.`
    : Number(s.difference)
      ? `الفرق مع الحسابات ليس صفراً (${dirhams(s.difference)}): ابحث عن عملية مسجَّلة مرتين أو بمبلغ خاطئ.`
      : null;

  return (
    <div className="desk-split">
      <section className="desk-page" ref={fitRef}>
        <PageHeader
          breadcrumbs={[{ label: 'كشوف البنك', to: '/bank' }]}
          title={
            <>
              {s.account?.name} <Period from={s.periodStart} to={s.periodEnd} />
            </>
          }
          status={STATUS[s.status]}
          actions={
            <>
              {s.documentPhoto && (
                <Button
                  icon={<FilePdfOutlined />}
                  href={s.documentPhoto.url}
                  target="_blank"
                >
                  الكشف
                </Button>
              )}
              {canWrite && open && (
                <Tooltip title={closeBlocked}>
                  <Button
                    type="primary"
                    icon={<LockOutlined />}
                    disabled={!!closeBlocked}
                    onClick={() =>
                      act(
                        () =>
                          client
                            .mutation(
                              CloseStatementDocument,
                              { id },
                              bankContext,
                            )
                            .toPromise(),
                        'أُغلق الكشف',
                      )
                    }
                  >
                    إغلاق الكشف
                  </Button>
                </Tooltip>
              )}
              {canWrite && !open && (
                <Button
                  icon={<UnlockOutlined />}
                  onClick={async () => {
                    const ok = await modal.confirm({
                      title: 'إعادة فتح الكشف',
                      content:
                        'تصبح عملياته المطابقة قابلة للتعديل من جديد. أغلقه بعد التصحيح.',
                      okText: 'إعادة الفتح',
                      cancelText: 'رجوع',
                    });
                    if (ok) {
                      await act(
                        () =>
                          client
                            .mutation(
                              ReopenStatementDocument,
                              { id },
                              bankContext,
                            )
                            .toPromise(),
                        'أُعيد فتح الكشف',
                      );
                    }
                  }}
                >
                  إعادة فتح
                </Button>
              )}
              {canWrite && open && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={async () => {
                    const ok = await modal.confirm({
                      title: 'حذف الكشف',
                      content:
                        'تُحذف أسطره ومطابقاتها. العمليات والتحويلات المسجَّلة تبقى. لا يمكن التراجع.',
                      okText: 'احذف',
                      okButtonProps: { danger: true },
                      cancelText: 'رجوع',
                    });
                    if (
                      ok &&
                      (await act(
                        () =>
                          client
                            .mutation(
                              DeleteStatementDocument,
                              { id },
                              bankContext,
                            )
                            .toPromise(),
                        'حُذف الكشف',
                      ))
                    ) {
                      navigate('/bank');
                    }
                  }}
                >
                  حذف
                </Button>
              )}
            </>
          }
        />

        <div className="desk-kpis">
          <div className="desk-kpi">
            <span className="field-hint">الرصيد الافتتاحي</span>
            <strong>{dirhams(s.openingBalance)}</strong>
          </div>
          <div className="desk-kpi">
            <span className="field-hint">الرصيد الختامي</span>
            <strong>{dirhams(s.closingBalance)}</strong>
          </div>
          <div className="desk-kpi">
            <span className="field-hint">الفرق مع الحسابات</span>
            <strong className={Number(s.difference) ? 'is-debit' : 'is-credit'}>
              {dirhams(s.difference)}
            </strong>
          </div>
          <div className="desk-kpi">
            <span className="field-hint">الأسطر</span>
            <strong className="ltr-num">
              {s.matchedCount} / {s.matchedCount + s.unmatchedCount}
            </strong>
            <span className="field-hint">
              {s.unmatchedCount
                ? `بقي ${linesCount(s.unmatchedCount)}`
                : 'كلها مطابقة أو متجاهَلة'}
            </span>
          </div>
        </div>

        <IdTag id={s.id} label="معرّف الكشف" link />

        <Segmented<string>
          value={all ? 'all' : 'left'}
          onChange={(v) =>
            setParams(
              (p) => {
                const n = new URLSearchParams(p);
                n.set('show', v);
                return n;
              },
              { replace: true },
            )
          }
          options={[
            { value: 'left', label: `المتبقية (${s.unmatchedCount})` },
            { value: 'all', label: `الكل (${lines.length})` },
          ]}
        />

        <Table<Line>
          rowKey="id"
          size="small"
          tableLayout="fixed"
          dataSource={shown}
          pagination={false}
          scroll={{ y: 'calc(100vh - 330px)' }}
          rowClassName={(l) =>
            l.id === split.panel ? 'is-open is-clickable' : 'is-clickable'
          }
          onRow={(l) => ({ onClick: () => split.open(l.id) })}
          locale={{
            emptyText: all ? 'لا أسطر' : 'لم يبق سطر: كلها مطابقة أو متجاهَلة',
          }}
          columns={fit<Line>([
            {
              key: 'no',
              drop: 1,
              title: '#',
              width: 50,
              render: (_, l) => <span className="ltr-num">{l.lineNo}</span>,
            },
            {
              key: 'date',
              title: 'التاريخ',
              width: 100,
              render: (_, l) => <span className="ltr-num">{l.date}</span>,
            },
            {
              key: 'label',
              title: 'بيان البنك',
              ellipsis: true,
              render: (_, l) => (
                <span>
                  {l.label}
                  {l.reference && (
                    <span className="field-hint ltr-num"> · {l.reference}</span>
                  )}
                </span>
              ),
            },
            {
              key: 'amount',
              title: 'المبلغ',
              width: 140,
              render: (_, l) => <Amount amount={l.amount} />,
            },
            {
              key: 'state',
              title: 'في الحسابات',
              width: 240,
              ellipsis: true,
              render: (_, l) => <LineState line={l} />,
            },
          ])}
        />
      </section>

      {line && (
        <LinePanel
          key={line.id}
          line={line}
          statement={s}
          canWrite={canWrite && open}
          project={project}
          onProject={setProject}
          act={act}
          onDone={() => split.open(next(line.lineNo))}
          onClose={split.close}
        />
      )}
    </div>
  );
}

function LineState({ line: l }: { line: Line }) {
  if (l.matchedOperation) {
    return (
      <span>
        <CheckCircleFilled className="is-credit" />{' '}
        {l.matchedOperation.description || l.matchedOperation.category?.name}
      </span>
    );
  }
  if (l.matchedTransfer) {
    return (
      <span>
        <CheckCircleFilled className="is-credit" /> تحويل:{' '}
        {l.matchedTransfer.fromAccount?.name} ←{' '}
        {l.matchedTransfer.toAccount?.name}
      </span>
    );
  }
  if (l.ignored) {
    return <span className="field-hint">متجاهَل: {l.ignoreReason}</span>;
  }
  return <Tag color="orange">بلا مطابقة</Tag>;
}

type Act = (
  run: () => Promise<{ error?: Parameters<typeof describeError>[0] }>,
  done: string,
) => Promise<boolean>;

function LinePanel({
  line: l,
  statement: s,
  canWrite,
  project,
  onProject,
  act,
  onDone,
  onClose,
}: {
  line: Line;
  statement: Statement;
  canWrite: boolean;
  /** The project chosen last on this statement, proposed again. */
  project: string | null;
  onProject: (id: string | null) => void;
  act: Act;
  onDone: () => void;
  onClose: () => void;
}) {
  const client = useClient();
  const state = lineState(l);
  const type = isNegative(l.amount) ? 'debit' : 'credit';
  const [{ data }] = useQuery({
    query: SuggestionsDocument,
    variables: { id: l.id },
    pause: state !== 'open',
    context: useMemo(() => bankContext, []),
    requestPolicy: 'cache-and-network',
  });
  const suggestions = present(data?.bankStatementLine?.suggestedMatches.nodes);
  const [{ data: lists }] = useLists();
  const [mode, setMode] = useState<'operation' | 'transfer' | 'ignore'>(
    'operation',
  );
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [counterpartyId, setCounterpartyId] = useState<string | null>(null);
  const [description, setDescription] = useState(l.label);
  const [otherAccount, setOtherAccount] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const others = present(lists?.financialAccounts?.nodes).filter(
    (a) => a.id !== s.accountId,
  );

  const run = async (
    call: () => Promise<{ error?: Parameters<typeof describeError>[0] }>,
    done: string,
    then = onDone,
  ) => {
    setBusy(true);
    const ok = await act(call, done);
    setBusy(false);
    if (ok) then();
  };

  return (
    <DeskPanel
      title={
        <span>
          السطر <span className="ltr-num">{l.lineNo}</span>:{' '}
          <Amount amount={l.amount} />
        </span>
      }
      recordId={l.id}
      onClose={onClose}
    >
      <div className="desk-line-card">
        <div className="ltr-num">{l.date}</div>
        <div>{l.label}</div>
        {l.reference && <div className="field-hint ltr-num">{l.reference}</div>}
        {l.balance && (
          <div className="field-hint">الرصيد بعده: {dirhams(l.balance)}</div>
        )}
      </div>

      {!canWrite && state === 'open' && (
        <Alert
          type="info"
          showIcon
          message={
            s.status === 'open'
              ? 'للاطلاع فقط: أمين المال يطابق الأسطر.'
              : 'الكشف مغلق.'
          }
        />
      )}

      {state === 'matched' && (
        <div className="desk-form">
          <Alert
            type="success"
            showIcon
            message={
              l.matchedOperation ? (
                <Link to={`/operations?panel=${l.matchedOperation.id}`}>
                  {l.matchedOperation.date} ·{' '}
                  {l.matchedOperation.description ||
                    l.matchedOperation.category?.name ||
                    TYPE_LABELS[type]}
                  {l.matchedOperation.project &&
                    ` · ${l.matchedOperation.project.name}`}
                </Link>
              ) : (
                <Link to={`/transfers?panel=${l.matchedTransfer?.id}`}>
                  تحويل {l.matchedTransfer?.date}:{' '}
                  {l.matchedTransfer?.fromAccount?.name} ←{' '}
                  {l.matchedTransfer?.toAccount?.name}
                </Link>
              )
            }
          />
          {canWrite && (
            <Button
              loading={busy}
              onClick={() =>
                run(
                  () =>
                    client
                      .mutation(
                        UnmatchLineDocument,
                        { lineId: l.id },
                        bankContext,
                      )
                      .toPromise(),
                  'فُكّت المطابقة',
                  () => {},
                )
              }
            >
              فكّ المطابقة
            </Button>
          )}
        </div>
      )}

      {state === 'ignored' && (
        <div className="desk-form">
          <Alert type="info" showIcon message={`متجاهَل: ${l.ignoreReason}`} />
          {canWrite && (
            <Button
              loading={busy}
              onClick={() =>
                run(
                  () =>
                    client
                      .mutation(
                        IgnoreLineDocument,
                        { lineId: l.id, ignored: false },
                        bankContext,
                      )
                      .toPromise(),
                  'أُلغي التجاهل',
                  () => {},
                )
              }
            >
              إلغاء التجاهل
            </Button>
          )}
        </div>
      )}

      {state === 'open' && canWrite && (
        <>
          <h3 className="desk-panel-subtitle">مسجَّل من قبل؟</h3>
          {!data && <Skeleton active paragraph={{ rows: 2 }} />}
          {data && suggestions.length === 0 && (
            <div className="field-hint">
              لا عملية ولا تحويل بنفس المبلغ على هذا الحساب في 7 أيام. سجّله
              أسفله.
            </div>
          )}
          {suggestions.map((m) => (
            <div key={m.id} className="desk-suggestion">
              <span>
                <Tag>{m.kind === 'transfer' ? 'تحويل' : 'عملية'}</Tag>
                <span className="ltr-num">{m.date}</span>
                {m.daysApart > 0 && (
                  <span className="field-hint">
                    {' '}
                    (فارق <span className="ltr-num">{m.daysApart}</span> يوم)
                  </span>
                )}
                <div>{m.description || '—'}</div>
              </span>
              <Button
                type="primary"
                loading={busy}
                onClick={() =>
                  run(
                    () =>
                      client
                        .mutation(
                          MatchLineDocument,
                          {
                            lineId: l.id,
                            ...(m.kind === 'transfer'
                              ? { transferId: m.id }
                              : { operationId: m.id }),
                          },
                          bankContext,
                        )
                        .toPromise(),
                    'طُوبق السطر',
                  )
                }
              >
                مطابقة
              </Button>
            </div>
          ))}

          <h3 className="desk-panel-subtitle">غير مسجَّل</h3>
          <Segmented<typeof mode>
            block
            value={mode}
            onChange={setMode}
            options={[
              { value: 'operation', label: TYPE_LABELS[type] },
              { value: 'transfer', label: 'تحويل' },
              { value: 'ignore', label: 'تجاهل' },
            ]}
          />
          {mode === 'operation' && (
            <div className="desk-form">
              <div className="field-hint">
                يُسجَّل {TYPE_LABELS[type]} على {s.account?.name} بتاريخ السطر
                ومبلغه. الكشف هو الإثبات، فلا يلزم وصل.
              </div>
              <ProjectPicker
                placeholder="المشروع (إلزامي)"
                value={project}
                onChange={onProject}
              />
              <CategoryPicker
                type={type}
                placeholder="الصنف"
                value={categoryId}
                onChange={setCategoryId}
              />
              <CounterpartyPicker
                type={type}
                placeholder="الجهة"
                value={counterpartyId}
                onChange={setCounterpartyId}
              />
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="البيان"
              />
              <Button
                type="primary"
                loading={busy}
                disabled={!project}
                onClick={() =>
                  project &&
                  run(
                    () =>
                      client
                        .mutation(
                          OperationFromLineDocument,
                          {
                            lineId: l.id,
                            projectId: project,
                            categoryId,
                            counterpartyId,
                            description: description.trim() || null,
                          },
                          bankContext,
                        )
                        .toPromise(),
                    `سُجّل ${TYPE_LABELS[type]} وطُوبق`,
                  )
                }
              >
                تسجيل ومطابقة
              </Button>
            </div>
          )}
          {mode === 'transfer' && (
            <div className="desk-form">
              <div className="field-hint">
                {type === 'credit'
                  ? 'مال دخل البنك من حساب آخر، مثلاً إيداع نقدي من الصندوق.'
                  : 'مال خرج من البنك إلى حساب آخر، مثلاً سحب إلى الصندوق.'}
              </div>
              <Select
                placeholder={type === 'credit' ? 'جاء من حساب' : 'ذهب إلى حساب'}
                value={otherAccount ?? undefined}
                onChange={setOtherAccount}
                options={others.map((a) => ({ value: a.id, label: a.name }))}
              />
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="البيان"
              />
              <Button
                type="primary"
                loading={busy}
                disabled={!otherAccount}
                onClick={() =>
                  otherAccount &&
                  run(
                    () =>
                      client
                        .mutation(
                          TransferFromLineDocument,
                          {
                            lineId: l.id,
                            otherAccountId: otherAccount,
                            description: description.trim() || null,
                          },
                          bankContext,
                        )
                        .toPromise(),
                    'سُجّل التحويل وطُوبق',
                  )
                }
              >
                تسجيل التحويل ومطابقته
              </Button>
            </div>
          )}
          {mode === 'ignore' && (
            <div className="desk-form">
              <div className="field-hint">
                لسطر لا يُسجَّل في الحسابات. يبقى مبلغه في الفرق.
              </div>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="السبب (إلزامي)"
              />
              <Button
                loading={busy}
                disabled={!reason.trim()}
                onClick={() =>
                  run(
                    () =>
                      client
                        .mutation(
                          IgnoreLineDocument,
                          {
                            lineId: l.id,
                            ignored: true,
                            reason: reason.trim(),
                          },
                          bankContext,
                        )
                        .toPromise(),
                    'تُجوهل السطر',
                  )
                }
              >
                تجاهل السطر
              </Button>
            </div>
          )}
        </>
      )}
    </DeskPanel>
  );
}
